import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useLazyLoading } from '../../hooks/useLazyLoading';
import { imageCacheService } from '../../services/imageCacheService';
import { performanceMonitor } from '../../utils/performanceMonitor';
import styles from '../../styles/components/image/LazyImage.module.css';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  onClick?: () => void;
  loading?: 'lazy' | 'eager';
  placeholder?: string;
  onLoad?: () => void;
  onError?: () => void;
}

/**
 * Lazy loading image component with proper loading states and accessibility
 */
const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  className = '',
  onClick,
  loading = 'lazy',
  placeholder,
  onLoad,
  onError,
}) => {
  const [imageState, setImageState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [shouldLoad, setShouldLoad] = useState(loading === 'eager');
  const imageRef = useRef<HTMLImageElement>(null);

  // Use intersection observer for lazy loading
  const { targetRef, isIntersecting } = useLazyLoading(
    () => {
      if (!shouldLoad) {
        setShouldLoad(true);
      }
    },
    {
      threshold: 0.1,
      rootMargin: '50px',
      enabled: loading === 'lazy' && !shouldLoad,
    }
  );

  // Handle image load success
  const handleImageLoad = useCallback(() => {
    setImageState('loaded');
    performanceMonitor.endImageLoad(src, true);
    onLoad?.();
  }, [onLoad, src]);

  // Handle image load error
  const handleImageError = useCallback(() => {
    setImageState('error');
    performanceMonitor.endImageLoad(src, false);
    onError?.();
  }, [onError, src]);

  // Handle keyboard interaction for clickable images
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  }, [onClick]);

  // Start loading when component mounts if eager loading
  useEffect(() => {
    if (loading === 'eager') {
      setShouldLoad(true);
    }
  }, [loading]);

  // Trigger loading when intersection is detected
  useEffect(() => {
    if (isIntersecting && loading === 'lazy') {
      setShouldLoad(true);
      performanceMonitor.trackLazyLoadIntersection(src, true);
    }
  }, [isIntersecting, loading, src]);

  // Start performance tracking when loading begins
  useEffect(() => {
    if (shouldLoad) {
      const isCached = imageCacheService.isCached(src);
      performanceMonitor.startImageLoad(src, isCached);
    }
  }, [shouldLoad, src]);

  const containerClasses = [
    styles.lazyImageContainer,
    className,
    onClick ? styles.clickable : '',
    imageState === 'loading' ? styles.loading : '',
    imageState === 'error' ? styles.error : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={targetRef}
      className={containerClasses}
      onClick={onClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? `View image: ${alt}` : undefined}
    >
      {/* Loading placeholder */}
      {imageState === 'loading' && (
        <div className={styles.placeholder} aria-hidden="true">
          {placeholder ? (
            <img src={placeholder} alt="" className={styles.placeholderImage} />
          ) : (
            <div className={styles.loadingSpinner}>
              <div className={styles.spinner} />
            </div>
          )}
        </div>
      )}

      {/* Error state */}
      {imageState === 'error' && (
        <div className={styles.errorState} role="img" aria-label={`Failed to load image: ${alt}`}>
          <div className={styles.errorIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                fill="currentColor"
              />
            </svg>
          </div>
          <span className={styles.errorText}>Failed to load image</span>
        </div>
      )}

      {/* Actual image */}
      {shouldLoad && (
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          className={`${styles.image} ${imageState === 'loaded' ? styles.visible : ''}`}
          onLoad={handleImageLoad}
          onError={handleImageError}
          loading="eager" // We handle lazy loading ourselves
        />
      )}

      {/* Click overlay for interactive images */}
      {onClick && imageState === 'loaded' && (
        <div className={styles.clickOverlay} aria-hidden="true">
          <div className={styles.expandIcon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
                fill="currentColor"
              />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
};

export default LazyImage;